import {ApolloQueryResult} from '@apollo/client';
import React, {useMemo, useRef, useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import {FieldDefinitionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import notifications from 'notifications';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  CREATE_LIBRARY_PARAMETER_DEFINITION_ENDED_EVENT,
  CREATE_LIBRARY_PARAMETER_DEFINITION_STARTED_EVENT,
  UPDATE_LIBRARY_PARAMETER_DEFINITION_ENDED_EVENT,
  UPDATE_LIBRARY_PARAMETER_DEFINITION_STARTED_EVENT,
} from 'performance/processProceduresEvents';
import * as Keyboard from 'util/Keyboard';

import {
  createLibraryParameterDefinition,
  updateLibraryParameterDefinition,
} from '../network/LibraryParameterDefinitionDAO';
import {LibraryParameterDefinition, LibraryParameterDefinitionDataType} from '../ParameterLibraryTypes';
import {ParameterLibraryListingQuery} from './__generated/ParameterLibraryListingQuery';
import {
  ParameterDefinitionEditMode,
  validateParameterDefinitionDraftChanges,
} from './parameterLibraryListingUtils';

export interface DraftLibraryParameterDefinition {
  name: string;
  dataType: LibraryParameterDefinitionDataType;
  description: string;
  unitTypeId: string | null;
}

const EMPTY_DRAFT: DraftLibraryParameterDefinition = {
  name: '',
  dataType: FieldDefinitionType.FLOAT,
  description: '',
  unitTypeId: null,
};

export interface UseParameterDefinitionDraftArgs {
  parameterDefinitions: Array<LibraryParameterDefinition>;
  refetch: () => Promise<ApolloQueryResult<ParameterLibraryListingQuery>>;
  markParameterDefinitionRowsForAttention: (ids: Array<string>) => void;
}

export interface ParameterDefinitionDraftResult {
  editMode: ParameterDefinitionEditMode;
  isEditing: boolean;
  isCreating: boolean;
  isEditingExisting: boolean;
  editingDefinitionId: string | null;
  isSaving: boolean;

  draftParameterDefinition: DraftLibraryParameterDefinition;
  updateDraftParameterDefinition: (updates: Partial<DraftLibraryParameterDefinition>) => void;

  nameErrorMessage: string | null;
  descriptionErrorMessage: string | null;
  unitTypeErrorMessage: string | null;
  isValid: boolean;

  startCreating: () => void;
  startEditingDefinition: (definition: LibraryParameterDefinition) => void;
  onCancel: () => void;
  onSave: () => Promise<void>;
  handleRowKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => Promise<void>;
}

/**
 * Manages draft state, validation, and save logic for both creating and editing
 * parameter library definitions. Tracks a ParameterDefinitionEditMode ('none' | 'create' | 'edit')
 * so the listing component can render the appropriate UI for each state.
 */
export function useParameterDefinitionDraft({
  parameterDefinitions,
  refetch,
  markParameterDefinitionRowsForAttention,
}: UseParameterDefinitionDraftArgs): ParameterDefinitionDraftResult {
  const [editMode, setEditMode] = useState<ParameterDefinitionEditMode>('none');
  const [isSaving, setIsSaving] = useState(false);
  const [draft, setDraft] = useState<DraftLibraryParameterDefinition>(EMPTY_DRAFT);
  const [editingDefinitionId, setEditingDefinitionId] = useState<string | null>(null);
  const originalFieldsRef = useRef({name: '', description: ''});

  const isEditing = editMode !== 'none';
  const isCreating = editMode === 'create';
  const isEditingExisting = editMode === 'edit';

  const {existingNames, existingArchivedNames} = useMemo(
    () => ({
      existingNames: new Set(parameterDefinitions.map((pd) => pd.name)),
      existingArchivedNames: new Set(
        parameterDefinitions.filter((pd) => pd.archiveData.archived).map((pd) => pd.name)
      ),
    }),
    [parameterDefinitions]
  );

  const updateDraft = (updates: Partial<DraftLibraryParameterDefinition>): void => {
    setDraft((current) => ({...current, ...updates}));
  };

  const trimmedName = draft.name.trim();
  const trimmedDescription = draft.description.trim();

  const {nameErrorMessage, descriptionErrorMessage, unitTypeErrorMessage, isValid} =
    validateParameterDefinitionDraftChanges({
      editMode,
      trimmedName,
      trimmedDescription,
      dataType: draft.dataType,
      unitTypeId: draft.unitTypeId,
      existingNames,
      existingArchivedNames,
      originalName: originalFieldsRef.current.name,
      originalDescription: originalFieldsRef.current.description,
    });

  const startCreating = (): void => {
    if (isEditing || isSaving) {
      return;
    }

    originalFieldsRef.current = {name: '', description: ''};
    setDraft(EMPTY_DRAFT);
    setEditMode('create');
    setEditingDefinitionId(null);
  };

  const startEditingDefinition = (definition: LibraryParameterDefinition): void => {
    if (isEditing || isSaving) {
      return;
    }
    const name = definition.name;
    const description = definition.description ?? '';
    originalFieldsRef.current = {name, description};
    setDraft({
      name,
      dataType: definition.dataType,
      description,
      unitTypeId: definition.unitType?.id ?? null,
    });
    setEditMode('edit');
    setEditingDefinitionId(definition.id);
  };

  const resetDraftState = (): void => {
    setEditMode('none');
    setEditingDefinitionId(null);
    setDraft(EMPTY_DRAFT);
  };

  const onCancel = (): void => {
    if (isSaving) {
      return;
    }
    resetDraftState();
  };

  async function onSave(): Promise<void> {
    assert(isValid, 'onSave called with invalid draft state', {
      extra: {nameErrorMessage, descriptionErrorMessage, unitTypeErrorMessage},
    });

    // Protects against multiple calls to onSave on fast clicking to save the changes.
    if (isSaving) {
      return;
    }
    // Capture before state resets so the catch block shows the right error message.
    const wasCreating = isCreating;
    setIsSaving(true);
    try {
      if (isCreating) {
        await handleCreate();
      } else if (isEditingExisting) {
        await handleUpdate();
      } else {
        assert(false, 'onSave should only be called in create or edit mode. This is an unexpected state.', {
          extra: {
            isCreating,
            isEditingExisting,
          },
        });
      }

      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (_e: unknown) {
      // We do not use the error here as it is better to show the generic error message to the user in this case.
      notifications.error(
        `Something went wrong while ${wasCreating ? 'creating' : 'updating'} the parameter. Please try again.`
      );
    } finally {
      setIsSaving(false);
    }
  }

  /** Handles creating a parameter definition.
   *
   * When saving a create, we reset the draft state after the API call given in the case of API call failure,
   * the draft row will stay visible and user can try again or copy the inputs it previously filled in.
   *
   */
  async function handleCreate(): Promise<void> {
    const profileEventId = uuidv4();
    logProfilerTimingEvent({...CREATE_LIBRARY_PARAMETER_DEFINITION_STARTED_EVENT, id: profileEventId});
    const createResult = await createLibraryParameterDefinition({
      ...draft,
      name: trimmedName,
      description: trimmedDescription,
    });
    resetDraftState();
    await refetch();
    markParameterDefinitionRowsForAttention(createResult.createdIds);
    notifications.success(
      <>
        Parameter <strong>{trimmedName}</strong> added
      </>
    );
    logProfilerTimingEvent({...CREATE_LIBRARY_PARAMETER_DEFINITION_ENDED_EVENT, id: profileEventId});
  }

  /** Handles updating a parameter definition.
   *
   * When saving an update, we reset the draft state before the API call given the row is persisted
   * in the old or new state regardless of the API call's success or failure.
   *
   */
  async function handleUpdate(): Promise<void> {
    assert(editingDefinitionId != null, 'Editing definition ID is required');
    const profileEventId = uuidv4();
    logProfilerTimingEvent({...UPDATE_LIBRARY_PARAMETER_DEFINITION_STARTED_EVENT, id: profileEventId});
    resetDraftState();
    const updateResult = await updateLibraryParameterDefinition({
      id: editingDefinitionId,
      name: trimmedName,
      description: trimmedDescription,
    });
    await refetch();
    markParameterDefinitionRowsForAttention(updateResult.updatedIds);
    notifications.success(
      <>
        Parameter <strong>{trimmedName}</strong> updated
      </>
    );
    logProfilerTimingEvent({...UPDATE_LIBRARY_PARAMETER_DEFINITION_ENDED_EVENT, id: profileEventId});
  }

  async function handleRowKeyDown(e: React.KeyboardEvent<HTMLInputElement>): Promise<void> {
    if (Keyboard.isOSCtrlKey(e) && Keyboard.isEnterKey(e) && isValid && !isSaving) {
      e.preventDefault();
      await onSave();
    }
  }

  return {
    editMode,
    isEditing,
    isCreating,
    isEditingExisting,
    editingDefinitionId,
    isSaving,
    draftParameterDefinition: draft,
    updateDraftParameterDefinition: updateDraft,
    nameErrorMessage,
    descriptionErrorMessage,
    unitTypeErrorMessage,
    isValid,
    startCreating,
    startEditingDefinition,
    onCancel,
    onSave,
    handleRowKeyDown,
  };
}
